import * as React from "react";
import styled, { css } from "styled-components";
import * as moment from "moment-timezone";

import { Text } from "src/library/components/Text/Text";
import { Icon } from "src/library/components/Icon";
import { Tooltip } from "src/library/components/Tooltip";
import { Select } from "src/library/components/Forms";
import { Icons } from "src/library/components/Icon/types";
import { CutoffTimeSelection } from "src/core/shared/components/CutoffTimeSelection";
import { getDefaultLeadTimeCutOff } from "src/core/orders/utils/order-deliveries";
import {
  IDeliveryCutoffSetting,
  IDeliveryWeekLeadTimesSetting,
  ILeadTimeDeliveryWeek,
  WeekDay
} from "src/core/drops/api/drops.api";
import { IProductLeadTimeGroup } from "src/core/products/api/products.api";

import useTimezone from "./time-helpers/useTimeZone";

interface UseLeadTimeDayRowProps {
  defaultCutoff: IDeliveryCutoffSetting | null;
  currentLeadTimeConfiguration: IDeliveryWeekLeadTimesSetting | null;
  currentLeadTimeGroup?: IProductLeadTimeGroup;
  disabled?: boolean;
  setCurrentLeadTimeConfiguration: (configuration: IDeliveryWeekLeadTimesSetting) => void
}

/**
 * One day row in the leadtimes table
 */
export const useLeadTimeDayRow = ({
  currentLeadTimeConfiguration, currentLeadTimeGroup, setCurrentLeadTimeConfiguration, defaultCutoff, disabled
}: UseLeadTimeDayRowProps) => {
  const timezone = useTimezone();
  const leadTimeWeekDeliveries: ILeadTimeDeliveryWeek = currentLeadTimeConfiguration?.deliveryWeekLeadTimes && currentLeadTimeGroup ? currentLeadTimeConfiguration.deliveryWeekLeadTimes[ currentLeadTimeGroup.name ] : {};

  const weekDays = [
    1,
    2,
    3,
    4,
    5,
    6,
    7
  ];

  /**
    * Creates a human readable version explaing the settings row
    */
  const generateNaturalLanguage = React.useCallback((deliveryWeekDay: string) => {
    if (currentLeadTimeConfiguration && currentLeadTimeGroup) {
      const leadTimeWeek = currentLeadTimeConfiguration.deliveryWeekLeadTimes[ currentLeadTimeGroup.name ];

      if (leadTimeWeek) {
        const day = leadTimeWeek[ deliveryWeekDay ];
        const orderByDay = leadTimeWeek[ deliveryWeekDay ].day;
        const deliveryDOW = parseInt(deliveryWeekDay, 10);

        const deliveryWeekdayName = moment
          .utc()
          .isoWeekday(parseInt(deliveryWeekDay, 10))
          .format("dddd");

        const orderByWeekdayName = moment
          .utc()
          .isoWeekday(day.day)
          .format("dddd");

        const orderByCutoffTime = moment
          .utc(day.cutoffTime, "HH:mm")
          .format("HH:mm");

        const orderByWeek = day.week ? `${day.week} week${day.week > 1 ? "s" : ""} before at` : "before";
        const leadTime = ((deliveryDOW - orderByDay + 7) % 7) + ((day.week || 0) * 7);

        if (day) {
          return `${leadTime} day lead time - For deliveries on ${deliveryWeekdayName} with a lead time group of ${currentLeadTimeGroup.name}, orders must be placed ${orderByWeek} ${orderByCutoffTime} on ${orderByWeekdayName}`;
        }
      }
    }

    return "";
  },
  [ currentLeadTimeConfiguration, currentLeadTimeGroup ]
  );

  if (!currentLeadTimeGroup) {
    return null;
  }

  return Object.keys(leadTimeWeekDeliveries).map(weekDay => [
    // Week day delivery.
    <DayCell key={`day-${weekDay}`}>
      <div
        data-tip
        data-for={`${weekDay}-tooltip`}
      >
        <Text className="day-text">
          {moment
            .utc()
            .isoWeekday(parseInt(weekDay))
            .format("dddd")}
        </Text>

        <Icon
          icon={Icons.HELP}
          size={10}
        />
      </div>

      <Tooltip
        id={`${weekDay}-tooltip`}
        type="dark"
        place="top"
        getContent={() => {
          return (
            <Text color="white">
              {generateNaturalLanguage(weekDay)}
            </Text>
          );
        }}
      />
    </DayCell>,
    // Week day cutoff.
    <Select
      key={`${weekDay}-select-day`}
      as="select"
      value={leadTimeWeekDeliveries[ weekDay ].day}
      className="select-box"
      name={`${weekDay}`}
      disabled={disabled}
      autoHideLabels
      label=""
      options={weekDays.map(weekDayOption => {
        let label = moment
          .utc()
          .isoWeekday(weekDayOption)
          .format("dddd");

        label =
            weekDayOption === getDefaultLeadTimeCutOff(currentLeadTimeGroup.defaultLeadTimeDays, parseInt(weekDay) as WeekDay) ? `${label} (default)` : label;

        return {
          text: label,
          value: weekDayOption.toString()
        };
      })}
      handleChange={selectProps => {
        const leadTimeConf: IDeliveryWeekLeadTimesSetting | null = Object.assign({}, currentLeadTimeConfiguration);

        leadTimeConf.deliveryWeekLeadTimes[ currentLeadTimeGroup.name ][ weekDay ].day = parseInt(selectProps.currentTarget.value) as WeekDay;
        setCurrentLeadTimeConfiguration(leadTimeConf);
      }}
    />,

    <Select
      key={`${weekDay}-select-week`}
      as="select"
      value={leadTimeWeekDeliveries[ weekDay ].week?.toString() || "0"}
      className="select-box"
      name={`${weekDay}-week`}
      disabled={disabled}
      autoHideLabels
      label=""
      options={[
        {
          text: "Same week",
          value: "0"
        },
        {
          text: "Previous week",
          value: "1"
        },
        {
          text: "2 weeks before",
          value: "2"
        },
        {
          text: "3 weeks before",
          value: "3"
        }
      ]}
      handleChange={selectProps => {
        const leadTimeConf: IDeliveryWeekLeadTimesSetting | null = Object.assign({}, currentLeadTimeConfiguration);

        leadTimeConf.deliveryWeekLeadTimes[ currentLeadTimeGroup.name ][ weekDay ].week = parseInt(selectProps.currentTarget.value) as WeekDay;
        setCurrentLeadTimeConfiguration(leadTimeConf);
      }}
    />,

    <CutoffTimeSelection
      key="cutoff-selection"
      timezone={timezone}
      value={leadTimeWeekDeliveries[ weekDay ].cutoffTime || defaultCutoff?.time}
      name={`${weekDay}`}
      isDefaultValue={(!leadTimeWeekDeliveries[ weekDay ].cutoffTime || defaultCutoff?.time === leadTimeWeekDeliveries[ weekDay ].cutoffTime) ? true : false}
      disabled={disabled}
      setTime={time => {
        const leadTimeConf: IDeliveryWeekLeadTimesSetting | null = Object.assign({}, currentLeadTimeConfiguration);

        // if it matches the default then clear the time from the row
        if (time === defaultCutoff?.time) {
          delete leadTimeConf.deliveryWeekLeadTimes[ currentLeadTimeGroup.name ][ weekDay ].cutoffTime;
        } else {
          leadTimeConf.deliveryWeekLeadTimes[ currentLeadTimeGroup.name ][ weekDay ].cutoffTime = time;
        }

        setCurrentLeadTimeConfiguration(leadTimeConf);
      }}
    />
  ]);
};

const DayCell = styled.div`
  ${({ theme }) => css`
    .day-text {
      margin-right: ${theme.spacing.margins.xs};
    }
  `}
`;
